import { useCallback, useEffect, useState } from 'react';

import { ReaderMessageType } from '../../constants';
import type { Message, MessageHandler, MessageListener, SendMessage } from '../../types';

type SetReaderFrame = (readerFrame: HTMLIFrameElement) => void;

export const useSendReaderMessage: () => [SetReaderFrame, SendMessage] = () => {
    const [readerFrame, setReaderFrame] = useState<HTMLIFrameElement>();

    const sendMessage = useCallback(
        (msg, data) => {
            if (msg && readerFrame && readerFrame.contentWindow) {
                try {
                    if (msg === ReaderMessageType.goToPage) {
                        if (data) {
                            readerFrame.contentWindow.postMessage(
                                { type: msg, pageNumber: data },
                                '*',
                            );
                        }
                    } else {
                        readerFrame.contentWindow.postMessage({ type: msg }, '*');
                    }
                } catch (e) {
                    throw new Error(`Error communicating with reader: ${e}`);
                }
            }
        },
        [readerFrame],
    );
    return [setReaderFrame, sendMessage];
};

export const useReaderEventHandler: (
    readerURL: string,
    sendMessage: SendMessage,
    handler: MessageHandler,
) => void = (readerUrl, sendMessage, handler) => {
    useEffect(() => {
        const listener: MessageListener = event => {
            if (readerUrl.indexOf(event.origin) !== 0) {
                // Cannot test this due to lacking jsdom support
                return;
            }

            const message: Message = { ...event.data };
            handler(message);
            // Listen for 'reader3Available', then send 'STOP_MESSAGE_QUEUE'
            if (message.type === 'reader3Available') {
                sendMessage(ReaderMessageType.stopMessageQueue);
            }
        };

        sendMessage(ReaderMessageType.sendQueuedMessages);
        window.addEventListener('message', listener, false);
        return () => {
            window.removeEventListener('message', listener, false);
        };
    }, [sendMessage, handler]);
};
